import React, { useState, useEffect } from 'react';
import { Button, Form, Modal, Row, Col } from 'react-bootstrap';
import clientAxios from '../../clients/clientAxios';
import { notify } from 'react-notify-toast';
import QuillEditor from './QuillEditor';

export default function EditCareer({ show, onHide, carrera, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    durationMonths: '',
    modulesCount: '',
    courses: '',
    fullDescription: '',
    videoUrl: '',
    certificateCareerName: '',
    slugUrl: '',
    downloadablePdf: false,
    enable: true,
  });
  const [isSaving, setIsSaving] = useState(false);

  const headerConfig = {
    headers: { 'auth-token': localStorage.getItem('JWT') || ' ' },
  };

  useEffect(() => {
    if (carrera && show) {
      setFormData({
        name: carrera.name || '',
        description: carrera.description || '',
        durationMonths: carrera.durationMonths || '',
        modulesCount: carrera.modulesCount || '',
        courses: Array.isArray(carrera.courses)
          ? carrera.courses.join('\n')
          : '',
        fullDescription: carrera.fullDescription || '',
        videoUrl: carrera.videoUrl || '',
        certificateCareerName: carrera.certificateCareerName || '',
        slugUrl: carrera.slugUrl || '',
        downloadablePdf: Boolean(carrera.downloadablePdf),
        enable: carrera.enable !== undefined ? carrera.enable : true,
      });
    }
  }, [carrera, show]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((previousData) => ({
      ...previousData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFullDescriptionChange = (htmlContent) => {
    setFormData((previousData) => ({
      ...previousData,
      fullDescription: htmlContent,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.name.trim()) {
      notify.show('El nombre de la carrera es requerido', 'error');
      return;
    }

    if (!carrera?._id) {
      notify.show('Error: ID de la carrera no encontrado', 'error');
      return;
    }

    setIsSaving(true);

    try {
      const normalizedCourses = formData.courses
        .split('\n')
        .map((courseName) => courseName.trim())
        .filter((courseName) => Boolean(courseName));

      const payload = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim(),
        fullDescription: formData.fullDescription,
        videoUrl: formData.videoUrl.trim(),
        certificateCareerName: formData.certificateCareerName.trim(),
        slugUrl: formData.slugUrl.trim(),
        courses: normalizedCourses,
        durationMonths: formData.durationMonths
          ? parseInt(formData.durationMonths, 10)
          : undefined,
        modulesCount: formData.modulesCount
          ? parseInt(formData.modulesCount, 10)
          : undefined,
      };

      await clientAxios.put(`/carreers/${carrera._id}`, payload, headerConfig);
      notify.show('Carrera actualizada correctamente', 'success');
      onSave();
    } catch (error) {
      notify.show(
        error?.response?.data?.message || 'Error al actualizar la carrera',
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal centered show={show} onHide={onHide} size='lg'>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Carrera</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className='mb-3'>
            <Form.Label>
              Nombre de la Carrera <span className='text-danger'>*</span>
            </Form.Label>
            <Form.Control
              type='text'
              name='name'
              value={formData.name}
              onChange={handleChange}
              placeholder='Ej: Fullstack 9 meses'
              required
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Descripcion corta</Form.Label>
            <Form.Control
              as='textarea'
              name='description'
              value={formData.description}
              onChange={handleChange}
              placeholder='Breve descripcion de la carrera'
              rows={2}
            />
          </Form.Group>

          <Row>
            <Form.Group as={Col} className='mb-3'>
              <Form.Label>Duracion (meses)</Form.Label>
              <Form.Control
                type='number'
                name='durationMonths'
                value={formData.durationMonths}
                onChange={handleChange}
                placeholder='Ej: 9'
                min='1'
              />
            </Form.Group>

            <Form.Group as={Col} className='mb-3'>
              <Form.Label>Cantidad de modulos</Form.Label>
              <Form.Control
                type='number'
                name='modulesCount'
                value={formData.modulesCount}
                onChange={handleChange}
                placeholder='Ej: 3'
                min='1'
              />
            </Form.Group>
          </Row>

          <Form.Group className='mb-3'>
            <Form.Label>Cursos (uno por linea)</Form.Label>
            <Form.Control
              as='textarea'
              name='courses'
              value={formData.courses}
              onChange={handleChange}
              placeholder='Creacion y Diseno de paginas web...'
              rows={4}
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Descripcion completa (HTML)</Form.Label>
            <QuillEditor
              onChange={handleFullDescriptionChange}
              value={formData.fullDescription}
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>URL del video (YouTube embed)</Form.Label>
            <Form.Control
              type='text'
              name='videoUrl'
              value={formData.videoUrl}
              onChange={handleChange}
              placeholder='Ej: https://www.youtube.com/embed/...'
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Nombre para certificado de carrera</Form.Label>
            <Form.Control
              type='text'
              name='certificateCareerName'
              value={formData.certificateCareerName}
              onChange={handleChange}
              placeholder='Ej: Fullstack Web Developer 9 meses'
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Slug URL</Form.Label>
            <Form.Control
              type='text'
              name='slugUrl'
              value={formData.slugUrl}
              onChange={handleChange}
              placeholder='Ej: fullstack-9-meses'
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Check
              type='checkbox'
              name='downloadablePdf'
              label='PDF descargable habilitado'
              checked={formData.downloadablePdf}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Check
              type='checkbox'
              name='enable'
              label='Habilitar carrera'
              checked={formData.enable}
              onChange={handleChange}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant='secondary' onClick={onHide} disabled={isSaving}>
            Cerrar
          </Button>
          <Button variant='primary' type='submit' disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}